import FieldError from "../components/FieldError";

function buildDefaultAssignment(location) {
  return {
    location_id: location.id,
    is_available: true,
    sold_out: false,
    pickup_available: true,
    delivery_available: Boolean(location.accepts_delivery),
    price_override: "",
  };
}

function isLocationSelected(assignments, locationId) {
  return assignments.some((assignment) => assignment.location_id === locationId);
}

export default function MenuItemForm({
  categories,
  categoriesError,
  categoriesLoading,
  draft,
  fieldErrors = {},
  locations,
  locationsError,
  locationsLoading,
  onChange,
  onFileChange,
  onSubmit,
  submitting,
}) {
  const activeCategories = categories.filter((category) => category.is_active);
  const activeLocations = locations.filter((location) => location.is_active);
  const hasSelectedLocation = draft.location_assignments.length > 0;
  const selectedCategoryExists = activeCategories.some((category) => {
    return String(category.id) === String(draft.category);
  });
  const canSubmit = Boolean(
    draft.name &&
      draft.category &&
      draft.base_price &&
      hasSelectedLocation &&
      !categoriesLoading &&
      !locationsLoading &&
      selectedCategoryExists,
  );
  const noActiveLocations = !locationsLoading && !locationsError && !activeLocations.length;

  const updateAssignment = (locationId, changes) => {
    const nextAssignments = draft.location_assignments.map((assignment) => {
      if (assignment.location_id !== locationId) {
        return assignment;
      }

      return { ...assignment, ...changes };
    });

    onChange({ location_assignments: nextAssignments });
  };

  const toggleLocation = (location, checked) => {
    if (checked) {
      onChange({
        location_assignments: [
          ...draft.location_assignments,
          buildDefaultAssignment(location),
        ],
      });
      return;
    }

    onChange({
      location_assignments: draft.location_assignments.filter((assignment) => {
        return assignment.location_id !== location.id;
      }),
    });
  };

  return (
    <section className="card admin-panel">
      <header className="card-header">
        <h2>Create Menu Item</h2>
      </header>
      <div className="card-body flex flex-column gap-4">
        <div className="form-row">
          <label className="form-group">
            <span className="form-label">Item name *</span>
            <input
              value={draft.name}
              onChange={(event) => onChange({ name: event.target.value })}
            />
            <FieldError message={fieldErrors.name} />
          </label>

          <label className="form-group">
            <span className="form-label">Base price *</span>
            <input
              value={draft.base_price}
              onChange={(event) => onChange({ base_price: event.target.value })}
            />
            <FieldError message={fieldErrors.base_price} />
          </label>

          <label className="form-group">
            <span className="form-label">Category *</span>
            <select
              disabled={categoriesLoading || Boolean(categoriesError)}
              value={draft.category}
              onChange={(event) => onChange({ category: event.target.value })}
            >
              <option value="">
                {categoriesLoading ? "Loading categories" : "Choose category"}
              </option>
              {activeCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <FieldError message={fieldErrors.category || categoriesError} />
          </label>

          <label className="checkbox-line">
            <input
              type="checkbox"
              checked={draft.is_featured}
              onChange={(event) => {
                onChange({ is_featured: event.target.checked });
              }}
            />
            Featured
          </label>
        </div>

        <label className="form-group">
          <span className="form-label">Description</span>
          <textarea
            className="form-textarea"
            value={draft.description}
            onChange={(event) => onChange({ description: event.target.value })}
          />
          <FieldError message={fieldErrors.description} />
        </label>

        <fieldset className="admin-fieldset">
          <legend>Available at locations *</legend>

          {locationsLoading ? <p className="muted-text">Loading active locations.</p> : null}
          {locationsError ? <div className="alert alert-error">{locationsError}</div> : null}
          {noActiveLocations ? (
            <div className="alert alert-warning">
              No active locations are available. Create or activate a location first.
            </div>
          ) : null}

          <div className="location-assignment-list">
            {activeLocations.map((location) => {
              const selected = isLocationSelected(draft.location_assignments, location.id);
              const assignment = draft.location_assignments.find((currentAssignment) => {
                return currentAssignment.location_id === location.id;
              });

              return (
                <div className="location-assignment" key={location.id}>
                  <label className="checkbox-line location-assignment-title">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(event) => toggleLocation(location, event.target.checked)}
                    />
                    {location.name}
                  </label>

                  {selected ? (
                    <div className="location-assignment-controls">
                      <label className="checkbox-line">
                        <input
                          type="checkbox"
                          checked={assignment.is_available}
                          onChange={(event) => {
                            updateAssignment(location.id, {
                              is_available: event.target.checked,
                            });
                          }}
                        />
                        Available
                      </label>
                      <label className="checkbox-line">
                        <input
                          type="checkbox"
                          checked={assignment.sold_out}
                          onChange={(event) => {
                            updateAssignment(location.id, {
                              sold_out: event.target.checked,
                            });
                          }}
                        />
                        Sold out
                      </label>
                      <label className="checkbox-line">
                        <input
                          type="checkbox"
                          checked={assignment.pickup_available}
                          onChange={(event) => {
                            updateAssignment(location.id, {
                              pickup_available: event.target.checked,
                            });
                          }}
                        />
                        Pickup available
                      </label>
                      <label className="checkbox-line">
                        <input
                          type="checkbox"
                          checked={assignment.delivery_available}
                          onChange={(event) => {
                            updateAssignment(location.id, {
                              delivery_available: event.target.checked,
                            });
                          }}
                        />
                        Delivery available
                      </label>
                      <label className="form-group">
                        <span className="form-label">Price override</span>
                        <input
                          inputMode="decimal"
                          placeholder="Optional"
                          value={assignment.price_override || ""}
                          onChange={(event) => {
                            updateAssignment(location.id, {
                              price_override: event.target.value,
                            });
                          }}
                        />
                      </label>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <FieldError message={fieldErrors.location_assignments} />
        </fieldset>

        <label className="form-group">
          <span className="form-label">Optional image</span>
          <input
            type="file"
            accept="image/*"
            onChange={(event) => onFileChange(event.target.files?.[0] || null)}
          />
          <FieldError message={fieldErrors.image} />
        </label>

        <button
          className="btn btn-primary"
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          type="button"
        >
          {submitting ? "Creating" : "Create Item"}
        </button>
      </div>
    </section>
  );
}